/**
 * @file overlapUtils.js
 * @description Utilities for detecting overlapping containers in the portal page grid layout.
 * Coordinates use inclusive 0-based {row, col} pairs (start and end).
 */

/**
 * Returns true if two containers' grid rectangles intersect.
 * Both start and end positions are inclusive.
 *
 * @param {{ start: {row: number, col: number}, end: {row: number, col: number} }} a
 * @param {{ start: {row: number, col: number}, end: {row: number, col: number} }} b
 * @returns {boolean}
 */
function containersIntersect(a, b) {
    const rowOverlap = a.start.row <= b.end.row && a.end.row >= b.start.row;
    const colOverlap = a.start.col <= b.end.col && a.end.col >= b.start.col;
    return rowOverlap && colOverlap;
}

/**
 * Returns a Set of temporaryIds for all containers that overlap with at least one other container.
 *
 * @param {Array<{ temporaryId: any, start: {row: number, col: number}, end: {row: number, col: number} }>} containers
 * @returns {Set<any>}
 */
export function getOverlappingContainerIds(containers) {
    const overlapping = new Set();
    for (let i = 0; i < containers.length; i++) {
        for (let j = i + 1; j < containers.length; j++) {
            if (containersIntersect(containers[i], containers[j])) {
                overlapping.add(containers[i].temporaryId);
                overlapping.add(containers[j].temporaryId);
            }
        }
    }
    return overlapping;
}

/**
 * Returns true if any two containers in the array overlap.
 *
 * @param {Array<{ start: {row: number, col: number}, end: {row: number, col: number} }>} containers
 * @returns {boolean}
 */
export function hasContainerOverlap(containers) {
    for (let i = 0; i < containers.length; i++) {
        for (let j = i + 1; j < containers.length; j++) {
            if (containersIntersect(containers[i], containers[j])) {
                return true;
            }
        }
    }
    return false;
}
